import React, { useContext, useState } from 'react';
import { CustomFieldType } from '../../../model/model';
import Button from '@atlaskit/button';
import { DefaultOptionHelper } from './DefaultOptionHelper';
import Icon from '@atlaskit/icon';
import EditorRemoveIcon from '@atlaskit/icon/glyph/editor/remove';
import Tooltip from '@atlaskit/tooltip';
import { GlobalContext } from '../../common/GlobalContext';
import InlineDialog from '@atlaskit/inline-dialog';
import { StarFillIcon } from '../../common/CustomIcons';
import EditorPanelIcon from '@atlaskit/icon/glyph/editor/panel';
import { DTOCustomFieldOption, DTOGetDefaultOption } from 'generated-common';
import { token } from '@atlaskit/tokens';

export const DefaultOptionView = ({
  defaultOption,
  fieldType,
  allOptions,
  isLoading,
  onUnsetDefaultOption,
}: {
  customFieldId: string;
  contextId: number;
  defaultOption: DTOGetDefaultOption;
  fieldType: CustomFieldType;
  allOptions: DTOCustomFieldOption[];
  isLoading: boolean;
  onUnsetDefaultOption: () => void;
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { t } = useContext(GlobalContext);
  // NOTE: Cascading child option is lazy loaded! Invoke call done in SingleDefaultOptionByIdLazy react component!
  const hasSingleDefaultValue = DefaultOptionHelper.hasSingleDefaultValue(defaultOption, fieldType);
  const hasCascadingDefaultValues = DefaultOptionHelper.hasCascadingDefaultValues(defaultOption, fieldType);
  const hasMultipleDefaultValues = DefaultOptionHelper.hasMultipleDefaultValues(defaultOption, fieldType);
  const isUnsetDisabled = !(hasSingleDefaultValue || hasCascadingDefaultValues || hasMultipleDefaultValues);

  const noDefaultMessage = <div style={{ color: token('color.background.accent.gray.bolder', '#777') }}>{t('fieldEditor.defaultOption.noDefaultValue')}</div>;
  const defaultOptionContent = (
    <div data-e2e-default-value="true">
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', justifyContent: 'space-between' }}>
        <div style={{ fontWeight: 'bold', marginRight: '40px', display: 'flex', alignItems: 'center' }}>
          {t('fieldEditor.defaultOption.headline')}
          <Tooltip content={t('fieldEditor.defaultOption.types.' + fieldType)}>
            <EditorPanelIcon label="default" primaryColor={token('color.text.subtle', '#253858')} size="medium" />
          </Tooltip>
        </div>
        <Tooltip content={t('fieldEditor.deleteDefaultOptionButton')}>
          <span data-e2e-unset-default-value="true">
            <Button
              appearance="subtle"
              iconBefore={<EditorRemoveIcon label="unsetdefault" primaryColor={token('color.text.subtle', '#253858')} />}
              isDisabled={isUnsetDisabled || isLoading}
              onClick={() => onUnsetDefaultOption()}
            />
          </span>
        </Tooltip>
      </div>
      <div style={{ marginRight: '10px' }}>
        {hasSingleDefaultValue && <>{defaultOption!.defaultOption!.optionValue && <>{defaultOption!.defaultOption!.optionValue}</>}</>}
        {hasCascadingDefaultValues && (
          <>
            {defaultOption!.defaultOption!.optionValue ? (
              <>
                {defaultOption.defaultOption && <>{defaultOption.defaultOption.optionValue}</>}
                {defaultOption.defaultChildOption && (
                  <ul>
                    <li>{defaultOption.defaultChildOption.optionValue}</li>
                  </ul>
                )}
              </>
            ) : (
              noDefaultMessage
            )}
          </>
        )}
        {hasMultipleDefaultValues ? (
          <ul>
            {defaultOption!.defaultOptions!.map((option, index) => (
              <li key={`default-options-${index}`}>{option.optionValue}</li>
            ))}
          </ul>
        ) : null}
        {!hasSingleDefaultValue && !hasCascadingDefaultValues && !hasMultipleDefaultValues && <>{noDefaultMessage}</>}
      </div>
    </div>
  );

  return (
    <>
      <>
        <InlineDialog
          onClose={() => {
            setIsModalOpen(false);
          }}
          content={defaultOptionContent}
          isOpen={isModalOpen}
        >
          <Tooltip content={t('fieldEditor.defaultOption.tooltip')} position="top">
            <div data-e2e-show-default-value-button="true" style={{ width: '35px' }}>
              <Button
                iconBefore={
                  <Icon glyph={StarFillIcon} label="star filled" size="small" primaryColor={isModalOpen ? token('color.text.selected', '#fff') : token('color.text.accent.blue.bolder', '#253858')} />
                }
                isSelected={isModalOpen}
                appearance="subtle"
                shouldFitContainer={true}
                onClick={() => setIsModalOpen((prev) => !prev)}
              />
            </div>
          </Tooltip>
        </InlineDialog>
      </>
    </>
  );
};
